/* AccountCodePicker — searchable dropdown of active account codes.
 * Shows both the code and the name everywhere: "5100 – Food Assistance".
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, Search, BookOpen, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple pickers on the same page share one fetch
let accountsCache = null;
let accountsPromise = null;

const fetchAccounts = async () => {
  if (accountsCache) return accountsCache;
  if (accountsPromise) return accountsPromise;
  accountsPromise = axios
    .get(`${BACKEND_URL}/api/admin/chart-of-accounts`, { withCredentials: true })
    .then((r) => {
      accountsCache = r.data?.accounts || [];
      return accountsCache;
    })
    .finally(() => { accountsPromise = null; });
  return accountsPromise;
};

export const invalidateAccountsCache = () => { accountsCache = null; };

const AccountCodePicker = ({ value, onChange, typeFilter = null, disabled = false, ariaLabel = 'Account code' }) => {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => { fetchAccounts().then(setAccounts); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (typeFilter && a.type !== typeFilter) return false;
      if (!s) return true;
      return a.code.toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s);
    });
  }, [accounts, search, typeFilter]);

  const current = accounts.find((a) => a.code === value);

  return (
    <div ref={wrapRef} className="relative" data-testid="account-code-picker">
      <button type="button" onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`w-full px-2.5 py-1.5 border rounded text-xs inline-flex items-center justify-between gap-2 text-left ${disabled ? 'bg-[#F8F9FA] text-[#9CA3AF]' : 'border-[#E5E7EB] bg-white text-[#0A0A0A] hover:border-[#BA1826]/40'}`}
        aria-label={ariaLabel} data-testid="account-code-toggle">
        <span className="flex-1 min-w-0 truncate inline-flex items-center gap-1.5">
          <BookOpen className="w-3 h-3 text-[#9CA3AF]" />
          {current ? (
            <>
              <span className="font-mono font-bold">{current.code}</span>
              <span className="text-[#9CA3AF]">–</span>
              <span className="truncate">{current.name}</span>
            </>
          ) : (
            <span className="text-[#9CA3AF]">Choose account code…</span>
          )}
        </span>
        <ChevronDown className={`w-3 h-3 text-[#9CA3AF] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-hidden bg-white border border-[#E5E7EB] rounded-lg shadow-lg">
          <div className="p-2 border-b border-[#E5E7EB] flex items-center gap-1.5">
            <Search className="w-3 h-3 text-[#9CA3AF]" />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name…"
              className="flex-1 text-xs focus:outline-none"
              data-testid="account-code-search" />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-[#9CA3AF] italic">No accounts match.</li>
            )}
            {filtered.map((a) => (
              <li key={a.code}>
                <button type="button"
                  onClick={() => { onChange(a.code); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#F8F9FA] inline-flex items-center gap-2 ${value === a.code ? 'bg-[#FFF8F8] text-[#BA1826] font-semibold' : 'text-[#0A0A0A]'}`}
                  data-testid={`account-code-option-${a.code}`}>
                  <span className="font-mono font-bold w-12">{a.code}</span>
                  <span className="text-[#9CA3AF]">–</span>
                  <span className="flex-1 truncate">{a.name}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${a.type === 'revenue' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>{a.type}</span>
                  {value === a.code && <Check className="w-3 h-3" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AccountCodePicker;
